import { Classes, IPanel, PanelStack } from "@blueprintjs/core";
import { showError, showSuccess } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import { DogBreederSaveDto, DogsBreederApi, PersonGridDto } from "../../../../../../api";
import { useDialog } from "../../../../../../contexts";
import { useApiService, useTl } from "../../../../../../hooks";
import { ETLCodes } from "../../../../../../locales";
import { DogsBreederListPanel } from "./DogsBreederListPanel";

export interface IDogsBreederPanelStackProps {
  dogId: number;
  onSaved: () => void;
}

const Container = styled.div`
  flex: 1;
  height: 100%;
  & > .${Classes.PANEL_STACK} {
    height: 100%;
  }
`;

export const DogsBreederPanelStack: React.FunctionComponent<IDogsBreederPanelStackProps> = ({ dogId, onSaved }) => {
  const { t } = useTl();
  const api = useApiService(DogsBreederApi);
  const { showDialogPromise } = useDialog();
  const onSectionSelected = React.useCallback(
    async (data: PersonGridDto) => {
      const dialogResult = await showDialogPromise({
        message: t(ETLCodes.SaveBreederConfirmation),
        title: t(ETLCodes.Warning),
      });
      if (dialogResult !== "yes") return;
      try {
        await api.saveDogsBreeder(DogBreederSaveDto.fromJS({ dogId: dogId, personId: data.personId }));
        showSuccess(t(ETLCodes.SaveSuccess));
      } catch {
        showError(t(ETLCodes.Error));
      }

      onSaved();
    },
    [api, dogId, onSaved, showDialogPromise, t]
  );

  const [panelStack, setPanelStack] = React.useState<IPanel[]>([
    {
      component: () => <DogsBreederListPanel onPersonSelected={onSectionSelected} dogId={dogId} />,
      title: t(ETLCodes.BreederSelection),
    },
  ]);

  const removeFromPanelStack = React.useCallback(() => {
    setPanelStack((prev) => prev.slice(0, prev.length - 1));
  }, [setPanelStack]);

  return (
    <Container>
      <PanelStack stack={panelStack} showPanelHeader={true} onClose={removeFromPanelStack}/>
    </Container>
  );
};
